import InfoIcon from "@/assets/icons/base64/InfoIcon"
import FireBgOrangeIcon from "@/assets/icons/fire_bg_orange.svg"
import OpenIcon from "@/assets/icons/open.svg"
import { selectedMainObligationAtom } from "@/atoms/obligations"
import { currentSlotAtom } from "@/atoms/pools"
import { networkAtom } from "@/atoms/settings"
import { selectedPoolAtom } from "@/atoms/shared"
import Button from "@/components/base/Button"
import { RelendTable } from "@/components/relendTable"
import EmptyRelendTable from "@/components/relendTable/emptyRelendTable"
import { Stat } from "@/components/stat"
import useMobileDevice from "@/hooks/useMobileDevice"
import useTrans from "@/hooks/useTrans"
import useWalletConnect from "@/hooks/useWalletConnect"
import { calculateNewBorrowedAmountAndCompoundedInterestRate } from "@/utils/actions/repay"
import { BORROW, REPAY, RE_NGN, RE_USD } from "@/utils/constants"
import { ObligationReserveType } from "@/utils/dataTypes"
import { TokenSymbol, tokenDecimalDigits } from "@/utils/helpers"
import { LOGO_SIZES, getTokenLogoDisplay } from "@/utils/logo"
import { RplToken } from "@/utils/modals/RplToken"
import { formatPercent, formatToken, formatUsd } from "@/utils/numberFormatter"
import {
  WHITELIST_SYMBOL_BORROW_ACTIONS,
  WHITELIST_SYMBOL_MY_BORROW_RESERVES,
  isSymbolWhitelisted,
} from "@/utils/whitelists"
import {
  Box,
  Divider,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material"
import { RE_VND, USDC, USDT, VND, calculateNewRatesAfterRefreshReserve } from "@renec-foundation/relend-adapter"
import BigNumber from "bignumber.js"
import { useAtom, useAtomValue } from "jotai"
import Image from "next/image"
import Link from "next/link"
import React, { useMemo } from "react"
import { formatToDecimals } from "../../../../utils/actions/repay"
import { getLinkExplorerAddress, getSupplyBorrowSortingList } from "../../commons"
import * as styles from "../styles"
import { BORROW_HEADER_VALUES, SelectBorrowHeader } from "../types"

const BorrowView = ({
  doesHaveBorrow,
  selectedBorrowHeader,
  borrowHeaderAprSelectItems,
  handleOpenBorrowRepayPopup,
  handleChangeSelectHeader,
}: {
  doesHaveBorrow: boolean
  selectedBorrowHeader: string
  borrowHeaderAprSelectItems: SelectBorrowHeader[]
  handleOpenBorrowRepayPopup: (item: ObligationReserveType, type: typeof BORROW | typeof REPAY) => void
  handleChangeSelectHeader: (event: SelectChangeEvent) => void
}) => {
  const trans = useTrans()
  const isMobileDevice = useMobileDevice()
  const { isSolanaChain } = useWalletConnect()
  const currentSlot = useAtomValue(currentSlotAtom)
  const [selectedPool] = useAtom(selectedPoolAtom)
  const [selectedObligation] = useAtom(selectedMainObligationAtom)
  const network = useAtomValue(networkAtom)

  const defaultBorrowTokenUSDT = useMemo(() => {
    const reserve = selectedPool?.reserves?.find((r) => r.symbol === RE_USD)

    return { reserveAddress: reserve?.address ?? "" } as ObligationReserveType
  }, [selectedPool])

  const myBorrowingHeaders = useMemo(
    () => (
      <>
        <TableCell>{trans.dashboard.table.asset}</TableCell>
        <TableCell>{trans.dashboard.table.borrowed}</TableCell>
        <TableCell>
          <Select value={selectedBorrowHeader} onChange={handleChangeSelectHeader} sx={styles.selectBox}>
            {borrowHeaderAprSelectItems.map((item, index) => (
              <MenuItem key={`${item.value}-${index}`} value={item.value}>
                <Typography sx={styles.statTitleGray}>{item.title}</Typography>
              </MenuItem>
            ))}
          </Select>
        </TableCell>
        <TableCell>{trans.dashboard.table.interest}</TableCell>
        <TableCell>{trans.dashboard.table.actions}</TableCell>
      </>
    ),
    [borrowHeaderAprSelectItems, handleChangeSelectHeader, selectedBorrowHeader, trans],
  )

  const desiredSymbolSortingOrder = useMemo(() => getSupplyBorrowSortingList(network), [network])

  const myBorrowingItems = useMemo(() => {
    if (!doesHaveBorrow || !selectedObligation) return <></>

    return selectedObligation.borrows
      .sort((a, b) => {
        const indexA = a.symbol ? desiredSymbolSortingOrder.indexOf(a.symbol) : -1
        const indexB = b.symbol ? desiredSymbolSortingOrder.indexOf(b.symbol) : -1

        // If the token is not in the desiredSymbolSortingOrder, move it to the end by assigning a high index value
        const maxIndex = desiredSymbolSortingOrder.length
        return (indexA === -1 ? maxIndex : indexA) - (indexB === -1 ? maxIndex : indexB)
      })
      .map((item, index) => {
        const symbol = item.symbol ?? ""
        const isUSDT = symbol === USDT
        const isUSDC = symbol === USDC

        if (!isSymbolWhitelisted(symbol, WHITELIST_SYMBOL_MY_BORROW_RESERVES)) {
          return <React.Fragment key={`${symbol}-my-borrow-${index}`}></React.Fragment>
        }
        const reserve = selectedPool?.reserves?.find((r) => r.address === item.reserveAddress)
        const isFiatToken = symbol === RE_VND || symbol === RE_NGN
        let repayMax = BigNumber(0)
        let valInterestAmount = 0

        if (reserve && currentSlot) {
          const { newCummulativeBorrowRate: newReserveCummulativeBorrowRate } = calculateNewRatesAfterRefreshReserve(
            reserve,
            currentSlot,
          )
          const { newBorrowedAmountAfterRefresh } = calculateNewBorrowedAmountAndCompoundedInterestRate(
            reserve,
            item,
            currentSlot,
          )
          const roundingStrategy = isFiatToken ? BigNumber.ROUND_UP : undefined
          valInterestAmount = newReserveCummulativeBorrowRate
            .dividedBy(item?.cumulativeBorrowRate)
            .minus(1)
            .multipliedBy(item?.borrowedAmountDecimals)
            .abs()
            .decimalPlaces(reserve.decimals, roundingStrategy)
            .toNumber()

          repayMax = formatToDecimals(newBorrowedAmountAfterRefresh, reserve.decimals)

          // TODO: update when script is done
          // valInterestAmount = newBorrowedAmountAfterRefresh
          //   .minus(item.principalBorrowedAmount)
          //   .abs()
          //   .decimalPlaces(reserve.decimals, roundingStrategy)
          //   .toNumber()
        }

        const borrowedAmount = item.amount ?? new BigNumber(0)
        const totalTokenBorrowed = borrowedAmount.isLessThan(repayMax) ? repayMax : borrowedAmount

        let marketPrice = formatUsd(reserve?.minPrice ?? 0)
        if (isFiatToken) {
          marketPrice = formatUsd(reserve?.minPrice ?? 0, 6)
        }

        const supplyBorrowAmount = formatToken(
          totalTokenBorrowed,
          tokenDecimalDigits[symbol as TokenSymbol],
          false,
          false,
          isFiatToken ? BigNumber.ROUND_UP : undefined,
          false,
          styles.supplyBorrowCell,
        )
        const borrowRate =
          selectedBorrowHeader === BORROW_HEADER_VALUES.apy ? reserve?.borrowAPYInterest : reserve?.borrowAPRInterest
        const borrowAPYFormatted = formatPercent(
          borrowRate ?? BigNumber(0),
          false,
          2,
          0.001,
          true,
          styles.supplyAPYCell,
        )
        const iconName = reserve ? RplToken.getTokenDisplayName(reserve.symbol, reserve.name) : null
        const interestsDisplay = (
          <Typography className='text-neutral-1000 font-semibold text-sm'>
            {formatToken(
              valInterestAmount,
              tokenDecimalDigits[symbol as TokenSymbol],
              false,
              false,
              isFiatToken ? BigNumber.ROUND_UP : undefined,
              false,
              styles.textBold,
            )}{" "}
            {iconName}
          </Typography>
        )

        const explorerMintURL = getLinkExplorerAddress(network, reserve?.mintAddress ?? "")
        const tokenNameDisplay = (
          <Link
            href={explorerMintURL}
            target='_blank'
            className='flex gap-1 items-center no-underline'
            data-testid={`my-supply-borrow-borrow-token-name-${index}`}
          >
            <Typography className='text-neutral-900 text-sm'>{iconName}</Typography>
            <Image className='grayscale' src={OpenIcon} alt='click-icon' width={12} height={12} />
            {item.symbol === RE_VND || item.symbol === VND ? (
              <Link
                href={"https://renec.org/blog/guide-to-borrow-repay-revnd"}
                target='_blank'
                className='flex gap-1 items-center no-underline'
              >
                <Image src={InfoIcon} alt='info-icon' width={16} height={16} />
              </Link>
            ) : null}
          </Link>
        )

        const borrowAPYDisplay =
          (isUSDT || isUSDC) && isSolanaChain ? (
            <Stack
              className='w-fit rounded-md border border-orange-highlight justify-center'
              direction='row'
              alignItems='center'
            >
              <Box className='px-2 text-green-600 font-bold text-sm'>{borrowAPYFormatted}</Box>
              <Image
                className='text-white rounded-tr-md rounded-br-md'
                src={FireBgOrangeIcon}
                alt='highlight-icon'
                width={24}
                height={24}
              />
            </Stack>
          ) : (
            <Typography className='text-green-600 font-bold text-sm'>{borrowAPYFormatted}</Typography>
          )

        if (isMobileDevice) {
          return (
            <Stack
              key={`${symbol}-my-borrow-${index}`}
              gap={1}
              className={`${index > 0 ? "border-t border-solid border-secondary pt-4 mt-4" : ""}`}
            >
              <Box sx={styles.iconWrapper}>
                {getTokenLogoDisplay(symbol, reserve, LOGO_SIZES.LG)}
                {tokenNameDisplay}
                <Divider
                  orientation='vertical'
                  flexItem
                  className='text-neutral-500'
                  style={{
                    marginTop: "6px",
                    height: "20px",
                    width: "2px",
                  }}
                />
                <Typography className='text-neutral-500 text-xs'>{marketPrice}</Typography>
              </Box>
              <Box>
                <Stat
                  statsWrapper={styles.statWrapper}
                  titleEl={<Typography sx={styles.statTitleGray}>{trans.dashboard.table.borrowed}</Typography>}
                  content={supplyBorrowAmount}
                  contentStyle={styles.supplyBorrowCell}
                  statsTitleWrapper={styles.statTitleMobileWrapper}
                />
                <Stat
                  statsWrapper={styles.statWrapper}
                  titleEl={
                    <Select value={selectedBorrowHeader} onChange={handleChangeSelectHeader} sx={styles.selectBox}>
                      {borrowHeaderAprSelectItems.map((item, index) => (
                        <MenuItem key={`${item.value}-${index}`} value={item.value}>
                          <Typography sx={styles.statTitleGray}>{item.title}</Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  }
                  content={borrowAPYDisplay}
                  contentStyle={styles.supplyAPYCell}
                  statsTitleWrapper={styles.statTitleMobileWrapper}
                />
                <Stat
                  statsWrapper={styles.statWrapper}
                  titleEl={<Typography sx={styles.statTitleGray}>{trans.dashboard.table.interest}</Typography>}
                  content={interestsDisplay}
                  contentStyle={styles.textLeft}
                  statsTitleWrapper={styles.statTitleMobileWrapper}
                />
              </Box>
              <Stack direction='row' alignItems='center' gap={1}>
                <Button
                  variant='primary'
                  onClick={() => {
                    handleOpenBorrowRepayPopup(item, BORROW)
                  }}
                  data-testid={`my-supply-borrow-borrow-button-${index}`}
                  className='flex-1'
                  size='md'
                >
                  {trans.dashboard.table.borrow}
                </Button>
                <Button
                  variant='secondary'
                  onClick={() => {
                    handleOpenBorrowRepayPopup(item, REPAY)
                  }}
                  data-testid={`my-supply-borrow-repay-button-${index}`}
                  className='flex-1'
                  size='md'
                >
                  {trans.dashboard.table.repay}
                </Button>
              </Stack>
            </Stack>
          )
        }

        return (
          <TableRow sx={styles.tableBodyRow} key={`${symbol}-my-borrow-${index}`}>
            <TableCell>
              <Stack direction='row' gap={1} alignItems='start'>
                {getTokenLogoDisplay(symbol, reserve, LOGO_SIZES.MD)}
                <Stack gap={0}>
                  {tokenNameDisplay}
                  <Typography className='text-neutral-500 text-xs'>{marketPrice}</Typography>
                </Stack>
              </Stack>
            </TableCell>
            <TableCell>
              <Typography className='text-neutral-900 font-semibold text-sm'>{supplyBorrowAmount}</Typography>
            </TableCell>
            <TableCell>{borrowAPYDisplay}</TableCell>
            <TableCell>{interestsDisplay}</TableCell>
            <TableCell>
              <Stack direction='row' justifyContent='space-between' alignItems='center' gap={1}>
                {isSymbolWhitelisted(symbol, WHITELIST_SYMBOL_BORROW_ACTIONS) && (
                  <Button
                    variant='primary'
                    onClick={() => {
                      handleOpenBorrowRepayPopup(item, BORROW)
                    }}
                    data-testid={`my-supply-borrow-borrow-button-${index}`}
                    className='flex-1'
                    size='md'
                  >
                    {trans.dashboard.table.borrow}
                  </Button>
                )}
                <Button
                  variant='secondary'
                  onClick={() => {
                    handleOpenBorrowRepayPopup(item, REPAY)
                  }}
                  data-testid={`my-supply-borrow-repay-button-${index}`}
                  className='flex-1'
                  size='md'
                >
                  {trans.dashboard.table.repay}
                </Button>
              </Stack>
            </TableCell>
          </TableRow>
        )
      })
  }, [
    trans,
    doesHaveBorrow,
    borrowHeaderAprSelectItems,
    currentSlot,
    handleChangeSelectHeader,
    handleOpenBorrowRepayPopup,
    isMobileDevice,
    selectedObligation,
    selectedPool,
    desiredSymbolSortingOrder,
    selectedBorrowHeader,
    network,
    isSolanaChain,
  ])

  return (
    <Stack>
      {doesHaveBorrow && !isMobileDevice && (
        <RelendTable
          loading={false}
          headers={myBorrowingHeaders}
          items={myBorrowingItems}
          loadingRows={10}
          loadingCellsPerRow={5}
        />
      )}

      {doesHaveBorrow && isMobileDevice && myBorrowingItems}

      {!doesHaveBorrow && (
        <EmptyRelendTable
          content={trans.dashboard.table.empty_borrow}
          buttonCTA={
            defaultBorrowTokenUSDT && (
              <Button
                size='md'
                className='min-w-[130px]'
                data-testid='my-borrow-empty-button'
                onClick={() => {
                  handleOpenBorrowRepayPopup(defaultBorrowTokenUSDT, BORROW)
                }}
              >
                {trans.dashboard.table.borrow}
              </Button>
            )
          }
        />
      )}
    </Stack>
  )
}

export default BorrowView
